import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Pagination } from '@/shared/components/custom/pagination/Pagination';
import { useServerPagination } from '@/shared/hooks/useServerPagination';
import { useOverdueTasks } from '../../hooks/useOverdueTasks';
import { OverdueTasksTableRow } from './OverdueTasksTableRow';
import type { OverdueTasksParams } from '../../types';

const SORT_OPTIONS: { value: NonNullable<OverdueTasksParams['sortBy']>; labelKey: string }[] = [
  { value: 'delayDays', labelKey: 'overdue.sort.delayDays' },
  { value: 'priority', labelKey: 'overdue.sort.priority' },
  { value: 'project', labelKey: 'overdue.sort.project' },
];

export function OverdueTasksTable() {
  const { t } = useTranslation('analytics');
  const [sortBy, setSortBy] = useState<OverdueTasksParams['sortBy']>('delayDays');

  const { data, isLoading } = useOverdueTasks({ sortBy, page: 1, pageSize: 10 });

  const pagination = useServerPagination({
    totalItems: data?.totalCount ?? 0,
    initialPageSize: 10,
  });

  const { data: pagedData } = useOverdueTasks({
    sortBy,
    page: pagination.apiParams.page,
    pageSize: pagination.apiParams.pageSize,
  });

  const items = pagedData?.items ?? data?.items ?? [];

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (!items.length) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">{t('empty.overdueTasks')}</p>
    );
  }

  return (
    <div className="space-y-3">
      {/* Sort toggle buttons */}
      <div className="flex items-center gap-2" data-testid="analytics-overdue-sort-controls">
        <span className="text-sm text-muted-foreground">{t('overdue.sort.label')}</span>
        <div className="flex gap-1.5">
          {SORT_OPTIONS.map(({ value, labelKey }) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={sortBy === value ? 'destructive' : 'outline'}
              className="h-7 px-3 text-xs"
              onClick={() => setSortBy(value)}
            >
              {t(labelKey)}
            </Button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto" data-testid="analytics-overdue-table">
        <table className="min-w-full table-auto">
          <thead>
            <tr className="border-b text-xs text-muted-foreground">
              <th className="py-2 pl-4 pr-2 text-left font-medium">{t('overdue.columns.task')}</th>
              <th className="px-2 py-2 text-left font-medium">{t('overdue.columns.project')}</th>
              <th className="px-2 py-2 text-left font-medium">{t('overdue.columns.assignee')}</th>
              <th className="px-2 py-2 text-left font-medium">{t('overdue.columns.priority')}</th>
              <th className="px-2 py-2 text-left font-medium">{t('overdue.columns.dueDate')}</th>
              <th className="px-2 py-2 pr-4 text-left font-medium">
                {t('overdue.columns.delayDays')}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((task) => (
              <OverdueTasksTableRow key={task.id ?? task.title} task={task} />
            ))}
          </tbody>
        </table>
      </div>

      {(pagedData?.totalCount ?? 0) > 10 && (
        <div data-testid="analytics-overdue-pagination">
          <Pagination {...pagination} />
        </div>
      )}
    </div>
  );
}
